import { SVGIcon } from '@superops/super-icons';
import { Button, TextStyle } from '@superops/ui-design-system';
import React, { useState } from 'react';
import { useActivityTracker } from '@nile/components';
import { isEmpty } from 'lodash';
import Tippy from '@tippyjs/react';
import 'tippy.js/animations/scale-extreme.css';
import SendReplyCard from './SendReplyCard';
import UpdateTicketCard from './UpdateTicketCard';
import DislikeCard from './DislikeCard';
import { FormattedMessage } from 'react-intl';
import ArticleReader from '@nile/rmm/src/modules/knowledge-base/components/ArticleSearchSheet/ArticleReader';
import { Sheet } from '@shopify/polaris';
import { useAIStore } from '@nile/components/src/Components/AI/store/aiStore';
import { useAIRecommendedSolutionsStore } from '@nile/components/src/Components/AI/store/aiRecommendedSolutionsStore.tsx';

interface TicketData {
  ticketId: string;
  displayId: string;
  subject: string;
  siteName: string;
}

interface ArticleData {
  id: string;
  title: string;
}

interface RecommendedSolution {
  title: string;
  summary: string;
  tickets: TicketData;
  kbArticles: ArticleData[];
}

interface RecommendedSolutionCardProps {
  data: RecommendedSolution;
  index: number;
  parentTicketId: string;
  openSimilarTicket: void;
  onDislike: void;
  openArticleSheet: (id: string) => void;
}

const ACTIONS = [
  {
    name: 'reply',
    label: <FormattedMessage id="common.send.reply" />,
    component: SendReplyCard,
    value: 'reply',
    mixpanelEvent: 'Clicked on Send reply from recommended solutions'
  },
  {
    name: 'update',
    label: <FormattedMessage id="common.update.ticket" />,
    component: UpdateTicketCard,
    value: 'update',
    mixpanelEvent: 'Clicked on update ticket from recommended solutions'
  }
];

const RecommendedSolutionCard: React.FC<RecommendedSolutionCardProps> = ({
  data,
  index,
  parentTicketId,
  openSimilarTicket,
  onDislike,
  openArticleSheet
}) => {
  const title = data?.title;
  const summary = data?.summary;
  const kbArticles = data?.kbArticles;
  const number = index + 1 || 0;

  const selectedTab = useAIRecommendedSolutionsStore((s) => s.selectedTab);
  const setSelectedTab = useAIRecommendedSolutionsStore(
    (s) => s.setSelectedTab
  );

  const sendRecommendedSolutionReplyResponse = useAIRecommendedSolutionsStore(
    (s) => s.sendRecommendedSolutionReplyResponse
  );

  const setSendRecommendedSolutionReplyResponse = useAIRecommendedSolutionsStore(
    (s) => s.setSendRecommendedSolutionReplyResponse
  );

  const sendRecommendedSolutionReplyData = useAIRecommendedSolutionsStore(
    (s) => s.sendRecommendedSolutionReplyData
  );

  const setSendRecommendedSolutionReplyData = useAIRecommendedSolutionsStore(
    (s) => s.setSendRecommendedSolutionReplyData
  );

  const sendReplyData = sendRecommendedSolutionReplyData?.[index];
  const sendReplyResponse = sendRecommendedSolutionReplyResponse?.[index];

  const onSendReplyData = (data) => {
    setSendRecommendedSolutionReplyData(index, data);
  };

  const onSendReplyResponse = (response) => {
    setSendRecommendedSolutionReplyResponse(index, response);
  };

  const selectedTabData = selectedTab?.[index];

  const tooltipRef = React.useRef(null);
  const [showDislike, setShowDislike] = useState(false);

  const dislike = () => {
    useActivityTracker({
      event: 'Clicked on solution not helpful from recommended solutions'
    });
    setShowDislike(true);
    setTimeout(() => {
      onDislike(data);
      setShowDislike(false);
    }, 1200);
  };

  const setShowRatingComponent = useAIStore((s) => s.setShowRatingComponent);

  const onCopy = () => {
    setShowRatingComponent(true);
  };

  const ActionComponent = selectedTabData?.component;

  const getMixpanelEventOnInsert = (name) => {
    switch (name) {
      case 'reply':
        return {
          event: 'Insert text from reply generated from recommended solutions',
          data: {
            ticketId: parentTicketId
          }
        };
      case 'update':
        setShowRatingComponent(true);
        return {
          event:
            'Insert properties from generated properties from recommended solutions',
          data: {
            ticketId: parentTicketId
          }
        };
      default:
        return null;
    }
  };

  const mixpanelEventOnInsert = getMixpanelEventOnInsert(selectedTabData?.name);

  if (showDislike) {
    return <DislikeCard />;
  }

  return (
    <div className="p10 br10 ai-dark-bg" id="ai-recommended-soln-card">
      <div className="flex fx-sb actr mb10">
        <div className="flex actr g10">
          <div className="flex">
            <SVGIcon name="AI_Bulb" />
          </div>
          <div>
            <TextStyle weight="Medium">
              <FormattedMessage id="common.recommended.solution" /> #{number}
            </TextStyle>
          </div>
        </div>
        {!isEmpty(data?.tickets) && (
          <div className="flex" ref={tooltipRef}>
            <Button
              aiOutline
              size="tiny"
              onClick={() => openSimilarTicket(data)}
            >
              <Tippy
                content={
                  <div className="ai-activator-tippy text-white">
                    <TextStyle>
                      <FormattedMessage id="ai.recommended.solution.view.source.tickets" />
                    </TextStyle>
                  </div>
                }
                zIndex={3947483000}
                appendTo={document.body}
                animation="scale-extreme"
                allowHTML
                className="ai-activator-tippy-wrapper"
                duration={200}
              >
                <div className="flex">
                  <SVGIcon name="Link" />
                </div>
              </Tippy>
            </Button>
          </div>
        )}
      </div>

      <div className="mb10">
        <TextStyle weight="Medium">{title}</TextStyle>
      </div>

      <div className="mb12">
        <TextStyle variation="aiSubdued">{summary}</TextStyle>
      </div>

      {kbArticles?.[0] && (
        <div className="mb12">
          <div className="fx g2">
            <div className="mt2">
              <SVGIcon color="#fff" name="Notes" size="sm" />
            </div>
            <div className="overflow-ellipses">
              <div>
                <TextStyle>
                  <FormattedMessage id="common.knowledge.base" />:
                </TextStyle>
              </div>
              {kbArticles?.map((article: ArticleData) => (
                <div
                  className="cp overflow-ellipses vividBlue"
                  onClick={() => openArticleSheet(article?.id)}
                >
                  {article?.title}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {!selectedTabData ? (
        <div className="flex actr g10 w100" id="action-tab-buttons">
          <div className="flex actr g10 w100">
            {ACTIONS.map((action) => {
              return (
                <div className="flex w100">
                  <Button
                    aiPrimary
                    icon={<SVGIcon name="AIWand" size="sm" />}
                    size="slim"
                    fullWidth
                    onClick={() => {
                      if (action?.mixpanelEvent) {
                        useActivityTracker({
                          event: action?.mixpanelEvent,
                          data: {
                            ticketId: parentTicketId
                          }
                        });
                      }
                      setSelectedTab(action, index);
                    }}
                  >
                    <div className="fsize12">{action?.label}</div>
                  </Button>
                </div>
              );
            })}
          </div>
          <div className="flex">
            <Button aiSecondary size="tiny" aiOutline onClick={dislike}>
              <div className="flex">
                <SVGIcon name="ThumbsDownOutline" />
              </div>
            </Button>
          </div>
        </div>
      ) : (
        <>
          <div className="flex actr g10" id="action-tab-pills">
            <div className="flex actr">
              <SVGIcon name="AIWand" />
            </div>
            {ACTIONS.map((action, idx) => {
              const headerValue = action?.value;
              const summaryTypeClass = [
                'ai-summary-type p5 pl10 pr10 br20 pointer white-color fs10 uppercase',
                headerValue === selectedTabData?.value && 'active'
                // isMonicaLoading && 'pointer-events-none'
              ]
                .filter(Boolean)
                .join(' ');
              return (
                <div
                  className={summaryTypeClass}
                  onClick={() => {
                    setSelectedTab(action, index);
                  }}
                  role="tab"
                  tabIndex={idx}
                  onKeyDown={() => {}}
                >
                  {action?.label}
                </div>
              );
            })}
          </div>

          <div className="mt10" id="action-component">
            <ActionComponent
              data={data}
              parentTicketId={parentTicketId}
              collapeOnInsert
              onCollapseComplete={() => {
                setSelectedTab(null, index);
              }}
              mixpanelEventOnInsert={mixpanelEventOnInsert}
              sendReplyData={sendReplyData}
              sendReplyResponse={sendReplyResponse}
              setSendReplyData={onSendReplyData}
              setSendReplyResponse={onSendReplyResponse}
              onCopy={onCopy}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default RecommendedSolutionCard;
